import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import {
  TrendingUp,
  FileText,
  Download,
  Calendar,
  CheckSquare,
  Square,
  ArrowLeft,
  Loader2,
  Settings,
} from 'lucide-react';
import { ThemeToggle } from '../components/ThemeToggle';
import PageSEO from '../components/PageSEO';
const METRIC_OPTIONS = [
  { id: 'overview', label: 'Overall Performance & Executive Summary', description: 'Users, views, sessions, bounce rate, engagement' },
  { id: 'traffic_sources', label: 'Traffic Sources / Referrers', description: 'Session source/medium analysis' },
  { id: 'devices', label: 'Device Categories', description: 'Desktop, Mobile, Tablet breakdown' },
  { id: 'browsers', label: 'Browser Distribution', description: 'Chrome, Safari, Firefox, Edge usage' },
  { id: 'top_pages', label: 'Top Pages by Views', description: 'Most visited pages comparison' },
  { id: 'geography', label: 'Geographic Distribution', description: 'Country-level visitor analysis' },
  { id: 'conversions', label: 'Conversion Performance', description: 'Signup, purchase, form submission tracking' },
];

const PRESETS = [
  {
    label: 'This Month vs Last Month',
    getRange: () => {
      const now = new Date();
      const aStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const aEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const bStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const bEnd = new Date(now.getFullYear(), now.getMonth(), 0);
      return { aStart, aEnd, bStart, bEnd, aLabel: formatMonth(aStart), bLabel: formatMonth(bStart) };
    },
  },
  {
    label: 'This Month vs Same Month Last Year',
    getRange: () => {
      const now = new Date();
      const aStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const aEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const bStart = new Date(now.getFullYear() - 1, now.getMonth(), 1);
      const bEnd = new Date(now.getFullYear() - 1, now.getMonth() + 1, 0);
      return { aStart, aEnd, bStart, bEnd, aLabel: formatMonth(aStart), bLabel: formatMonth(bStart) };
    },
  },
  {
    label: 'Last 7 Days vs Previous 7 Days',
    getRange: () => {
      const now = new Date();
      const aEnd = new Date(now);
      const aStart = new Date(now); aStart.setDate(aStart.getDate() - 7);
      const bEnd = new Date(aStart);
      const bStart = new Date(bEnd); bStart.setDate(bStart.getDate() - 7);
      return { aStart, aEnd, bStart, bEnd, aLabel: 'Last 7 days', bLabel: 'Previous 7 days' };
    },
  },
  {
    label: 'Last 30 Days vs Previous 30 Days',
    getRange: () => {
      const now = new Date();
      const aEnd = new Date(now);
      const aStart = new Date(now); aStart.setDate(aStart.getDate() - 30);
      const bEnd = new Date(aStart);
      const bStart = new Date(bEnd); bStart.setDate(bStart.getDate() - 30);
      return { aStart, aEnd, bStart, bEnd, aLabel: 'Last 30 days', bLabel: 'Previous 30 days' };
    },
  },
];

function formatMonth(d) {
  return d.toLocaleString('default', { month: 'long', year: 'numeric' });
}

function toISODate(d) {
  return d.toISOString().split('T')[0] + 'T00:00:00+00:00';
}

export default function ComparativeReportPage({ user, onLogout }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [periodA, setPeriodA] = useState({ start: '', end: '' });
  const [periodB, setPeriodB] = useState({ start: '', end: '' });
  const [periodALabel, setPeriodALabel] = useState('Period A');
  const [periodBLabel, setPeriodBLabel] = useState('Period B');
  const [selectedMetrics, setSelectedMetrics] = useState(METRIC_OPTIONS.map(m => m.id));
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);

  const allSelected = selectedMetrics.length === METRIC_OPTIONS.length;

  const toggleMetric = (id) => {
    setSelectedMetrics(prev =>
      prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    setSelectedMetrics(allSelected ? [] : METRIC_OPTIONS.map(m => m.id));
  };

  const applyPreset = (preset) => {
    const r = preset.getRange();
    setPeriodA({ start: r.aStart.toISOString().split('T')[0], end: r.aEnd.toISOString().split('T')[0] });
    setPeriodB({ start: r.bStart.toISOString().split('T')[0], end: r.bEnd.toISOString().split('T')[0] });
    setPeriodALabel(r.aLabel);
    setPeriodBLabel(r.bLabel);
  };

  const handleGenerate = useCallback(async () => {
    if (!periodA.start || !periodA.end || !periodB.start || !periodB.end) {
      toast.error('Please select both time periods');
      return;
    }
    if (selectedMetrics.length === 0) {
      toast.error('Please select at least one metric section');
      return;
    }

    setGenerating(true);
    setProgress(10);

    const progressInterval = setInterval(() => {
      setProgress(prev => Math.min(prev + Math.random() * 15, 90));
    }, 1500);

    try {
      const response = await axios.post(
        `/reports/comparative/${projectId}`,
        {
          period_a_start: toISODate(new Date(periodA.start)),
          period_a_end: toISODate(new Date(periodA.end)),
          period_b_start: toISODate(new Date(periodB.start)),
          period_b_end: toISODate(new Date(periodB.end)),
          period_a_label: periodALabel,
          period_b_label: periodBLabel,
          selected_metrics: selectedMetrics,
        },
        { responseType: 'blob' }
      );

      clearInterval(progressInterval);
      setProgress(100);

      // Download the file
      const contentType = response.headers['content-type'] || 'application/pdf';
      const ext = contentType.includes('pdf') ? 'pdf' : 'html';
      const blob = new Blob([response.data], { type: contentType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `comparative_report.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Report generated and downloaded!');
    } catch (error) {
      clearInterval(progressInterval);
      console.error('Report generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    } finally {
      setGenerating(false);
      setProgress(0);
    }
  }, [projectId, periodA, periodB, periodALabel, periodBLabel, selectedMetrics]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-background transition-colors">
      <PageSEO title="Comparative Report | AI Search Advisers" isPrivate={true} />
      {/* Top Bar */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10 lg:py-12 space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4 sm:mb-6">
          <h3 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">Comparative Report</h3>
          <Button 
            variant="outline" 
            onClick={() => navigate('/dashboard')} 
            className="w-full sm:w-auto shrink-0 min-h-11 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>

        {/* ===== STEP 1: TIME PERIODS ===== */}
        <Card className="p-4 sm:p-6 bg-white dark:bg-card border-slate-200 dark:border-border">
          <div className="flex items-center gap-3 mb-4 sm:mb-5">
            <div className="w-8 h-8 rounded-full bg-[#021426] text-white flex items-center justify-center text-sm font-bold shrink-0">1</div>
            <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">Select Time Periods</h2>
          </div>

          {/* Presets */}
          <div className="mb-4 sm:mb-5">
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mb-2 sm:mb-3">Quick presets:</p>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((preset, i) => (
                <button
                  key={i}
                  onClick={() => applyPreset(preset)}
                  className="px-3 sm:px-4 py-2 text-xs sm:text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:border-[#021426] hover:text-[#021426] dark:hover:border-[#FEAB3D] dark:hover:text-[#FEAB3D] transition-all"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {/* Period A */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                <Calendar size={14} className="inline mr-1" /> Period A (Current)
              </label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={periodA.start}
                  onChange={(e) => setPeriodA({ ...periodA, start: e.target.value })}
                  className="flex-1 min-w-0 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-xs sm:text-sm min-h-11"
                />
                <span className="self-center text-slate-400 text-xs sm:text-sm">to</span>
                <input
                  type="date"
                  value={periodA.end}
                  onChange={(e) => setPeriodA({ ...periodA, end: e.target.value })}
                  className="flex-1 min-w-0 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-xs sm:text-sm min-h-11"
                />
              </div>
              <input
                type="text"
                value={periodALabel}
                onChange={(e) => setPeriodALabel(e.target.value)}
                placeholder="Label (e.g., March 2026)"
                className="mt-2 w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-xs sm:text-sm min-h-11"
              />
            </div>

            {/* Period B */}
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                <Calendar size={14} className="inline mr-1" /> Period B (Comparison)
              </label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={periodB.start}
                  onChange={(e) => setPeriodB({ ...periodB, start: e.target.value })}
                  className="flex-1 min-w-0 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-xs sm:text-sm min-h-11"
                />
                <span className="self-center text-slate-400 text-xs sm:text-sm">to</span>
                <input
                  type="date"
                  value={periodB.end}
                  onChange={(e) => setPeriodB({ ...periodB, end: e.target.value })}
                  className="flex-1 min-w-0 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-xs sm:text-sm min-h-11"
                />
              </div>
              <input
                type="text"
                value={periodBLabel}
                onChange={(e) => setPeriodBLabel(e.target.value)}
                placeholder="Label (e.g., March 2025)"
                className="mt-2 w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-xs sm:text-sm min-h-11"
              />
            </div>
          </div>
        </Card>

        {/* ===== STEP 2: METRIC SELECTION ===== */}
        <Card className="p-4 sm:p-6 bg-white dark:bg-card border-slate-200 dark:border-border">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4 sm:mb-5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#021426] text-white flex items-center justify-center text-sm font-bold shrink-0">2</div>
              <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">Select Report Sections</h2>
            </div>
            <button
              onClick={toggleAll}
              className="flex items-center gap-2 text-xs sm:text-sm font-medium text-[#021426] dark:text-[#FEAB3D] hover:underline"
            >
              {allSelected ? <CheckSquare size={16} /> : <Square size={16} />}
              {allSelected ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          <div className="space-y-2">
            {METRIC_OPTIONS.map(opt => (
              <button
                key={opt.id}
                onClick={() => toggleMetric(opt.id)}
                className={`w-full flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border transition-all text-left ${
                  selectedMetrics.includes(opt.id)
                    ? 'border-[#021426] dark:border-[#FEAB3D] bg-[#f0fdf4] dark:bg-[#021426]/20'
                    : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600'
                }`}
              >
                {selectedMetrics.includes(opt.id)
                  ? <CheckSquare size={20} className="text-[#021426] dark:text-[#FEAB3D] flex-shrink-0" />
                  : <Square size={20} className="text-slate-400 flex-shrink-0" />
                }
                <div>
                  <div className="font-semibold text-slate-900 dark:text-white text-sm">{opt.label}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{opt.description}</div>
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* ===== STEP 3: GENERATE ===== */}
        <Card className="p-4 sm:p-6 bg-white dark:bg-card border-slate-200 dark:border-border">
          <div className="flex items-center gap-3 mb-4 sm:mb-5">
            <div className="w-8 h-8 rounded-full bg-[#021426] text-white flex items-center justify-center text-sm font-bold shrink-0">3</div>
            <h2 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">Generate Report</h2>
          </div>

          {/* Summary */}
          <div className="mb-4 sm:mb-5 p-3 sm:p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-xs sm:text-sm">
              <div>
                <span className="text-slate-500 dark:text-slate-400">Period A:</span>
                <span className="ml-2 font-medium text-slate-900 dark:text-white">
                  {periodA.start ? `${periodA.start} → ${periodA.end}` : 'Not set'}
                </span>
              </div>
              <div>
                <span className="text-slate-500 dark:text-slate-400">Period B:</span>
                <span className="ml-2 font-medium text-slate-900 dark:text-white">
                  {periodB.start ? `${periodB.start} → ${periodB.end}` : 'Not set'}
                </span>
              </div>
              <div>
                <span className="text-slate-500 dark:text-slate-400">Sections:</span>
                <span className="ml-2 font-medium text-slate-900 dark:text-white">
                  {selectedMetrics.length} of {METRIC_OPTIONS.length}
                </span>
              </div>
              <div>
                <span className="text-slate-500 dark:text-slate-400">Format:</span>
                <span className="ml-2 font-medium text-slate-900 dark:text-white">PDF (A4)</span>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          {generating && (
            <div className="mb-4 sm:mb-5">
              <div className="flex justify-between mb-1">
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  {progress < 30 ? 'Fetching analytics data...' :
                   progress < 60 ? 'Running AI analysis...' :
                   progress < 90 ? 'Rendering PDF layout...' :
                   'Finalizing report...'}
                </span>
                <span className="text-sm font-medium text-[#021426] dark:text-[#FEAB3D]">{Math.round(progress)}%</span>
              </div>
              <div className="w-full h-3 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500 ease-out"
                  style={{
                    width: `${progress}%`,
                    background: 'linear-gradient(90deg, #021426, #FEAB3D)',
                  }}
                />
              </div>
            </div>
          )}

          <Button
            onClick={handleGenerate}
            disabled={generating || !periodA.start || !periodA.end || !periodB.start || !periodB.end || selectedMetrics.length === 0}
            className="w-full bg-[#021426] hover:bg-[#0A4378] text-white py-4 sm:py-6 text-sm sm:text-base font-bold rounded-none shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
          >
            {generating ? (
              <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Generating Report...</>
            ) : (
              <><Download className="mr-2 h-5 w-5" /> Generate PDF Report</>
            )}
          </Button>

          <p className="text-xs text-slate-500 dark:text-slate-400 text-center mt-3">
            The report includes AI-generated executive summary and actionable recommendations.
            Generation may take 30-60 seconds.
          </p>
        </Card>
      </div>
    </div>
  );
}
